/**
 * What a person must act on for one Snapshot payments row (SnapshotPaymentsSection).
 *
 * AUDIT_VAL-70 M1/M2: `flags` - an upgrade whose credit was withdrawn by a refund, or a payment
 * for an option already held (refund due). Owner rulings 2026-10-31 09:19: `covered_requests` -
 * an option the owner asked for whose price earlier payments cover; it unlocks only when a
 * super_admin presses the confirm button here. All words come from the server
 * (WORDING FOR OWNER APPROVAL there). Classes are the row's existing ones: no colour change.
 * VAL-70-FOLLOWUPS F3: each flag with an id (`flagItems`) has "Mark resolved" - a reason of at
 * least 10 characters (the outside-Stripe rule), then the server records who and why.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/** Same rule and words as the outside-Stripe reason (SnapshotPaymentsSection). */
const MIN_REASON = 10
const REASON_TOO_SHORT = 'Give a reason of at least 10 characters.'

export interface CoveredRequest {
  request_id: string
  option: string
  title: string
  text: string
}

export interface FlagItem {
  flag_id: string
  text: string
}

interface Props {
  engagementId: string
  flags?: string[]
  /** VAL-70-FOLLOWUPS F3: the same flags with their ids; when given, each gets resolveLabel. */
  flagItems?: FlagItem[]
  resolveLabel?: string
  requests?: CoveredRequest[]
  confirmLabel: string
  /** Called after a confirm succeeded, so the section reloads the server's state. */
  onDone: () => void
}

export function SnapshotRowNotes({
  engagementId, flags, flagItems, resolveLabel = 'Mark resolved', requests, confirmLabel, onDone,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [resolving, setResolving] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const items: { flag_id?: string; text: string }[] = flagItems ?? (flags ?? []).map(text => ({ text }))

  async function resolve(flagId: string) {
    const trimmed = reason.trim()
    if (trimmed.length < MIN_REASON) { toast.error(REASON_TOO_SHORT); return }
    setBusy(true)
    try {
      await apiClient.post('/v1/admin/snapshot-payments/resolve-flag',
        { engagement_id: engagementId, flag_id: flagId, reason: trimmed })
      toast.success('Marked resolved.')
      setResolving(null); setReason('')
      onDone()
    } catch {
      toast.error('Failed to mark resolved.')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(option: string) {
    setBusy(true)
    try {
      await apiClient.post('/v1/admin/snapshot-payments/confirm-covered', { engagement_id: engagementId, option })
      toast.success('Payment recorded.')
      onDone()
    } catch {
      toast.error('Failed to record payment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {items.map((f, i) => (
        <div key={f.flag_id ?? i} className="text-xs">
          <span className="text-pal-error-text" data-testid={`snap-flag-${engagementId}-${i}`}>{f.text}</span>
          {f.flag_id && resolving !== f.flag_id && (
            <>
              {' '}
              <Button variant="outline" size="sm" disabled={busy}
                      onClick={() => { setResolving(f.flag_id ?? null); setReason('') }}>
                {resolveLabel}
              </Button>
            </>
          )}
          {f.flag_id && resolving === f.flag_id && (
            <span className="mt-1 flex items-center gap-2">
              <Input aria-label="Reason" value={reason} onChange={e => setReason(e.target.value)}
                     data-testid={`snap-resolve-reason-${engagementId}-${i}`} />
              <Button size="sm" disabled={busy} onClick={() => { void resolve(f.flag_id as string) }}>Confirm</Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => setResolving(null)}>Cancel</Button>
            </span>
          )}
        </div>
      ))}
      {(requests ?? []).map(q => (
        <div key={q.option} className="text-xs" data-testid={`snap-covered-${engagementId}-${q.option}`}>
          {q.text}{' '}
          <Button variant="outline" size="sm" disabled={busy} onClick={() => { void confirm(q.option) }}>
            {confirmLabel}
          </Button>
        </div>
      ))}
    </>
  )
}
